import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { Progress } from "reactstrap";

import { KEEP_DAYS, addDays, capacityPausedText, formatDay, formatShortDay } from "./studioText";

/* What a Studio plan has left, from a PlanView (docs/studio-plans-spec.md
   section 3.2): the AI message meter, the trial's day, the free repairs, the
   live apps and, from 75%, the fair-use meter in percent only. Never an
   amount of money.

   Each bar is named by its visible label (barAriaLabelledBy) and reads its
   value in words (barAriaValueText). Cyan bars: no red or amber, even when
   little is left. */

export const messagesTotal = (plan) =>
  (Number(plan?.messages?.included) || 0) + (Number(plan?.messages?.goodwill) || 0);

const PAID_BILLING = new Set(["active", "trialing"]);
const PAST_DUE = new Set(["past_due", "unpaid"]);

/**
 * The one sentence about the plan's state that needs the customer's
 * attention (past due, ending, offline...), or null. `billing` comes from the
 * backend's POST /studio/plan and may be missing.
 */
export function planBannerText(t, plan, billing, locale, now = Date.now()) {
  if (!plan) return null;
  const day = (iso) => formatDay(iso, locale);
  if (billing && PAST_DUE.has(billing.status)) {
    return plan.lapse?.offlineAt ? t("studio.plan.pastDue", { date: day(plan.lapse.offlineAt) }) : t("studio.plan.pastDueNoDate");
  }
  if (plan.status === "pending") return t("studio.plan.pending");
  if (plan.status === "suspended") return t("studio.plan.suspended");
  if (billing?.cancelAtPeriodEnd && PAID_BILLING.has(billing.status) && billing.renewsAt) {
    return t("studio.plan.ending", { date: day(billing.renewsAt) });
  }
  if (plan.status === "lapsed") {
    const offlineAt = plan.lapse?.offlineAt;
    if (offlineAt && Date.parse(offlineAt) > now) return t("studio.plan.lapsedGrace", { date: day(offlineAt) });
    return t("studio.plan.offline", { date: day(addDays(offlineAt || plan.lapse?.lapsedAt, KEEP_DAYS)) });
  }
  if (plan.status === "trial_ended") return t("studio.plan.trialEnded");
  if (plan.source === "comp" && plan.status === "active") return t("studio.plan.comp");
  return null;
}

/** The plan's state as a short label for a chip. */
export function planStatusLabel(t, plan, billing) {
  if (!plan) return "";
  if (billing && PAST_DUE.has(billing.status)) return t("studio.plan.status.past_due");
  if (billing?.cancelAtPeriodEnd && plan.status === "active") return t("studio.plan.status.ending");
  if (plan.source === "comp" && plan.status === "active") return t("studio.plan.status.comp");
  return t(`studio.plan.status.${plan.status || "none"}`, { defaultValue: plan.status });
}

function Bar({ id, label, value, valueText }) {
  return (
    <div className="studio-meter__row">
      <div className="studio-meter__label" id={id}>{label}</div>
      <Progress
        className="studio-meter__bar"
        value={Math.max(0, Math.min(100, value))}
        color="info"
        barAriaLabelledBy={id}
        barAriaValueText={valueText}
      />
    </div>
  );
}

function StudioMeter({ plan, locale, showPaused = true, showLiveApps = true }) {
  const { t } = useTranslation();
  const id = useId();
  if (!plan?.messages) return null;

  const total = messagesTotal(plan);
  const left = Math.max(0, Number(plan.messages.left) || 0);
  const trial = plan.trial;
  const valueText = t("studio.plan.messagesLeft", { left, total });

  let label = valueText;
  if (trial) {
    label = t("studio.plan.trialLine", {
      left,
      total,
      today: trial.todayUsed ?? 0,
      daily: trial.dailyLimit ?? 5,
      date: formatShortDay(trial.expiresAt, locale),
    });
  } else if (plan.resetsAt) {
    label = `${valueText} · ${t("studio.plan.resets", { date: formatShortDay(plan.resetsAt, locale) })}`;
  }

  const fixes = plan.fixes;
  const capacity = plan.capacity || {};
  const liveApps = plan.limits?.liveApps ?? 0;

  return (
    <div className="studio-meter">
      <Bar id={`${id}-messages`} label={label} value={total > 0 ? (left / total) * 100 : 0} valueText={valueText} />

      {fixes?.included > 0 && (
        <p className="studio-meter__line">{t("studio.plan.fixesLeft", { left: fixes.left ?? 0, total: fixes.included })}</p>
      )}

      {showLiveApps && liveApps > 0 && typeof plan.liveUsed === "number" && (
        <p className="studio-meter__line">{t("studio.plan.liveApps", { used: plan.liveUsed, total: liveApps })}</p>
      )}

      {capacity.showMeter && (
        <Bar
          id={`${id}-capacity`}
          label={t("studio.plan.capacity", { percent: capacity.percent ?? 0 })}
          value={capacity.percent ?? 0}
          valueText={t("studio.plan.capacity", { percent: capacity.percent ?? 0 })}
        />
      )}

      {showPaused && capacity.paused && (
        <div className="studio-notice studio-meter__paused">
          {/* On the trial resetsAt is the day it ends: no "until" a reset. */}
          <p>{capacityPausedText(t, plan, plan.resetsAt, locale)}</p>
        </div>
      )}
    </div>
  );
}

export default StudioMeter;
